import React, { useState } from 'react';
import { useEffect } from 'react';
import { Download, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { getInvoices } from '../../../utils/stripe/api';

interface BillingRecord {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'succeeded' | 'pending' | 'failed';
  invoice?: string;
}

export function BillingHistory() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore(state => state.user);

  const loadBillingHistory = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const invoices = await getInvoices(user.id);
      
      // Convert to BillingRecord format
      const records: BillingRecord[] = invoices.map((invoice: any) => ({
        id: invoice.id,
        date: invoice.date,
        description: invoice.description,
        amount: invoice.amount,
        status: invoice.status === 'paid' ? 'succeeded' : invoice.status,
        invoice: invoice.invoice
      }));
      
      setBillingHistory(records);
    } catch (err) {
      console.error('Failed to load billing history:', err);
      setError('請求履歴の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadBillingHistory();
    }
  }, [user?.id]);

  const displayRecords = isExpanded ? billingHistory : billingHistory.slice(0, 2);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">請求履歴</h3>
        <button
          onClick={loadBillingHistory}
          disabled={isLoading}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          title="更新"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-3"></div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      ) : billingHistory.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <p className="text-gray-500">請求履歴はありません</p>
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                日付
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                内容
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                金額
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ステータス
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                請求書
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayRecords.map((record) => (
              <tr key={record.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(record.date).toLocaleDateString('ja-JP')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {record.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ¥{record.amount.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    record.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                    record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {record.status === 'succeeded' ? '完了' :
                     record.status === 'pending' ? '処理中' : '失敗'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {record.invoice && (
                    <a href={record.invoice} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {billingHistory.length > 2 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-center gap-1 w-full py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              <span>履歴を折りたたむ</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              <span>すべての履歴を表示</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}